
import React, { memo, useContext, useEffect, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContext } from 'src/contexts/modal-context';
import { editableFields } from '../constants';
import { FormBuilder } from '@components/form';
import { translate } from '@components/i18n';
import { ItemResponse } from '@models/item';
import { customerSelector, fetchAllCustomers } from '@redux/slices/order';
import { Loading } from '@styles/components/loader';
import { createAuthorizedClient, fetchAuthorizedClients, removeAuthorizedClient } from '@redux/slices/authorized-clients';

interface EditModalProps {
  data?: ItemResponse;
}

export const EditModal: React.FC<EditModalProps> = memo(({ data }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm<ItemResponse>();
  const [editState, setEditState] = useState<ItemResponse>(data);
  const modalContext = useContext(ModalContext);
  const customerState = useSelector(customerSelector);

  const allItems = customerState.allCustomers
    .map(c => ({
      id: c.code,
      name: `${c.code} - ${c.name}`,
    })).removeDuplicatesById()

  useEffect(() => {
    dispatch(fetchAllCustomers());
  }, [])

  const onOk = (values): void => {
    if (!data) addNew(values);

    modalContext.handleClose();
  };

  const addNew = (values): void => {
    const orderKey = `${new Date().valueOf()}`;
    message.loading({
      content: translate('general.registeringItem'),
      key: orderKey,
      duration: 10,
      className: 'message-styled',
    });

    dispatch(
      createAuthorizedClient({
        data: {
          codigoCliente: values.client,
        },
        notificationKey: orderKey,
        onSuccess: () => {
          modalContext.handleClose();
          dispatch(fetchAuthorizedClients())
        },
      }),
    );
  };

  useEffect(() => {
    if (data) {
      const aux = {
        ...data,
        numeros: [`${data.numero}-x${data.multiplo || 1}`]

      }
      setEditState(aux);
      form.setFieldsValue(aux);
    } else {
      form.resetFields();
    }
  }, [data]);

  const onChangeValues = (
    changedValues: Partial<ItemResponse>,
    allValues: ItemResponse,
  ): void => {
    setEditState({
      ...allValues,
      ...changedValues,
    });
  };

  const handleDeleteUser = (): void => {
    const orderKey = `${new Date().valueOf()}`;
    dispatch(removeAuthorizedClient({
      data: data.id,
      notificationKey: orderKey,
      onSuccess: () => {
        dispatch(fetchAuthorizedClients())
      }
    }))
    modalContext.handleClose();
  };

  return (
    <Row align="middle">
      <Col span={1} />
      <Col span={22}>
        {customerState.isFetchingAllCustomers && <Loading />}
        <FormBuilder
          onValuesChange={onChangeValues}
          layout="vertical"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 24 }}
          initialValues={{ ...editState }}
          onFinish={onOk}
          form={form}
          fields={editableFields({
            editable: !data,
            optionsItems: allItems
          })}
          withDelete={!!data}
          onDelete={handleDeleteUser}
        />
      </Col>
      <Col span={1} />
    </Row>
  );
});
